/* eslint-disable react/prop-types */
import { useCallback, useEffect, useMemo, useState } from "react";
import Card from "@mui/material/Card";
import { Stack } from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import TableCommon from "components/TableCommon";
import { useGlobalFilter, usePagination, useSortBy, useTable } from "react-table";
import { CompanyService } from "services/companyService";
import { GeneralJournalService } from "services/general-journal";
import { formatDate, formatVndIntegerValue } from "utils";
import toast from "react-hot-toast";
import ActionCell from "./components/ActionCell";

const currentYear = String(new Date().getFullYear());

const tableData = {
  columns: [
    { Header: "Ngày ghi sổ", accessor: "recordDate", align: "center" },
    { Header: "Số CT", accessor: "voucherNumber", align: "center" },
    { Header: "Ngày CT", accessor: "voucherDate", align: "center" },
    { Header: "Diễn giải", accessor: "description" },
    { Header: "Sổ Cái", accessor: "postedToLedger", align: "center" },
    { Header: "Dòng", accessor: "lineNumber", align: "center" },
    { Header: "Tài khoản", accessor: "accountCode", align: "center" },
    { Header: "Nợ", accessor: "debit", align: "right" },
    { Header: "Có", accessor: "credit", align: "right" },
    { Header: "Actions", accessor: "action", sorted: false, align: "center" },
  ],
  rows: [],
};

function Filters({ filters, setFilters, onApply, onReset, onSync, syncing, loading }) {
  return (
    <SoftBox display="flex" flexDirection="column" justifyContent="space-between" alignItems="flex-start" p={3} pb={1}>
      <SoftBox lineHeight={1} width="100%">
        <Stack spacing={2} direction="row" flexWrap="wrap">
          <SoftBox display="flex" flexDirection="column">
            <SoftTypography component="label" variant="caption" fontWeight="bold" mb={0.5}>
              Năm
            </SoftTypography>
            <SoftBox width="8rem">
              <SoftInput
                placeholder="YYYY"
                value={filters.year}
                onChange={({ currentTarget }) => {
                  const next = currentTarget.value.replace(/\D/g, "").slice(0, 4);
                  setFilters((prev) => ({ ...prev, year: next }));
                }}
              />
            </SoftBox>
          </SoftBox>

          <SoftBox display="flex" flexDirection="column">
            <SoftTypography component="label" variant="caption" fontWeight="bold" mb={0.5}>
              Từ ngày
            </SoftTypography>
            <SoftInput
              type="date"
              value={filters.fromDate}
              onChange={({ currentTarget }) => setFilters((prev) => ({ ...prev, fromDate: currentTarget.value }))}
            />
          </SoftBox>

          <SoftBox display="flex" flexDirection="column">
            <SoftTypography component="label" variant="caption" fontWeight="bold" mb={0.5}>
              Đến ngày
            </SoftTypography>
            <SoftInput
              type="date"
              value={filters.toDate}
              onChange={({ currentTarget }) => setFilters((prev) => ({ ...prev, toDate: currentTarget.value }))}
            />
          </SoftBox>

          <SoftBox display="flex" flexDirection="column">
            <SoftTypography component="label" variant="caption" fontWeight="bold" mb={0.5}>
              Tìm kiếm
            </SoftTypography>
            <SoftBox width="16rem">
              <SoftInput
                placeholder="Số CT, diễn giải, tài khoản"
                value={filters.searchBy}
                onChange={({ currentTarget }) => setFilters((prev) => ({ ...prev, searchBy: currentTarget.value }))}
              />
            </SoftBox>
          </SoftBox>

          <SoftBox display="flex" alignItems="flex-end">
            <Stack direction="row" spacing={1}>
              <SoftBox
                component="button"
                onClick={onApply}
                disabled={loading}
                sx={{
                  height: "39px",
                  padding: "0 16px",
                  borderRadius: 0,
                  border: "none",
                  backgroundColor: "#1a73e8",
                  color: "white",
                  cursor: "pointer",
                  fontWeight: "bold",
                  opacity: loading ? 0.5 : 1,
                  "&:hover": { backgroundColor: "#1557b0" },
                }}
              >
                Tải dữ liệu
              </SoftBox>
              <SoftBox
                component="button"
                onClick={onReset}
                disabled={loading || syncing}
                sx={{
                  height: "39px",
                  padding: "0 16px",
                  borderRadius: 0,
                  border: "1px solid #d2d6da",
                  backgroundColor: "white",
                  color: "#344767",
                  cursor: "pointer",
                  fontWeight: "bold",
                  opacity: loading || syncing ? 0.5 : 1,
                  "&:hover": { backgroundColor: "#f8f9fa" },
                }}
              >
                Reset
              </SoftBox>
              <SoftBox
                component="button"
                onClick={onSync}
                disabled={syncing || loading}
                sx={{
                  height: "39px",
                  padding: "0 16px",
                  borderRadius: 0,
                  border: "none",
                  backgroundColor: "#4CAF50",
                  color: "white",
                  cursor: "pointer",
                  fontWeight: "bold",
                  opacity: syncing || loading ? 0.5 : 1,
                  "&:hover": { backgroundColor: "#388E3C" },
                }}
              >
                {syncing ? "Đang đồng bộ..." : "Đồng bộ dữ liệu"}
              </SoftBox>
            </Stack>
          </SoftBox>
        </Stack>
      </SoftBox>
    </SoftBox>
  );
}

export default function JournalEntries() {
  const [companyId, setCompanyId] = useState("");
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [dataTableState, setDataTableState] = useState(tableData);
  const [filters, setFilters] = useState({ year: currentYear, fromDate: "", toDate: "", searchBy: "" });
  const [query, setQuery] = useState({ year: currentYear, fromDate: "", toDate: "", searchBy: "", sortBy: "recordDate", sortType: "asc" });
  const [paginationData, setPaginationData] = useState({ page: 1, count: 0, size: 10 });
  const [summary, setSummary] = useState({ totalRows: 0, totalDebit: 0, totalCredit: 0, postedToLedgerCount: 0 });

  const columns = useMemo(() => dataTableState.columns, [dataTableState]);
  const data = useMemo(() => dataTableState.rows, [dataTableState]);
  const tableInstance = useTable({ columns, data, initialState: { pageIndex: 0 } }, useGlobalFilter, useSortBy, usePagination);

  useEffect(() => {
    const init = async () => {
      const response = await CompanyService.getByAuth();
      const payload = response?.data || response || {};
      setCompanyId(payload?._id || "");
    };
    init();
  }, []);

  const fetchData = useCallback(async () => {
    if (!companyId || !query.year) return;
    try {
      setLoading(true);
      const response = await GeneralJournalService.getJournalBook({
        company: companyId,
        year: Number(query.year),
        fromDate: query.fromDate || undefined,
        toDate: query.toDate || undefined,
        searchBy: query.searchBy || undefined,
        page: paginationData.page,
        limit: paginationData.size,
      });
      const payload = response?.data || response || {};
      const items = payload.items || [];
      setSummary(payload.summary || { totalRows: 0, totalDebit: 0, totalCredit: 0, postedToLedgerCount: 0 });
      setPaginationData((prev) => ({
        ...prev,
        count: payload.paginate?.count || 0,
        size: payload.paginate?.size || prev.size,
      }));
      const rows = items.map((item) => {
        const params = new URLSearchParams({
          year: String(query.year || currentYear),
          fromDate: String(query.fromDate || ""),
          toDate: String(query.toDate || ""),
          searchBy: String(query.searchBy || ""),
        }).toString();
        return {
          recordDate: formatDate(item.recordDate),
          voucherNumber: item.voucherNumber || "",
          voucherDate: formatDate(item.voucherDate),
          description: item.description || "",
          postedToLedger: item.postedToLedger ? "x" : "",
          lineNumber: item.lineNumber || "",
          accountCode: item.accountCode || "",
          debit: formatVndIntegerValue(item.debit || 0),
          credit: formatVndIntegerValue(item.credit || 0),
          action: <ActionCell item={{ viewLink: `/templates/journal-entries/view?${params}` }} />,
        };
      });
      setDataTableState((prev) => ({ ...prev, rows }));
    } catch (error) {
      toast.error(error?.response?.data?.message || "Không thể tải Sổ nhật ký chung");
      setDataTableState((prev) => ({ ...prev, rows: [] }));
      setSummary({ totalRows: 0, totalDebit: 0, totalCredit: 0, postedToLedgerCount: 0 });
      setPaginationData((prev) => ({ ...prev, count: 0 }));
    } finally {
      setLoading(false);
    }
  }, [companyId, query, paginationData.page, paginationData.size]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApply = () => {
    if (filters.fromDate && filters.toDate && filters.fromDate > filters.toDate) {
      toast.error("Khoảng thời gian không hợp lệ");
      return;
    }
    setPaginationData((prev) => ({ ...prev, page: 1 }));
    setQuery((prev) => ({ ...prev, ...filters }));
  };

  const handleReset = () => {
    const resetFilters = { year: currentYear, fromDate: "", toDate: "", searchBy: "" };
    setFilters(resetFilters);
    setPaginationData((prev) => ({ ...prev, page: 1 }));
    setQuery((prev) => ({ ...prev, ...resetFilters }));
  };

  const handleSync = async () => {
    if (!companyId || !filters.year) {
      toast.error("Thiếu thông tin công ty hoặc năm");
      return;
    }
    try {
      setSyncing(true);
      await GeneralJournalService.syncAppendix1({ company: companyId, year: Number(filters.year) });
      toast.success("Đồng bộ dữ liệu Phụ lục 1 sang Nhật ký chung thành công");
      setPaginationData((prev) => ({ ...prev, page: 1 }));
      setQuery((prev) => ({ ...prev, ...filters }));
      await fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Đồng bộ dữ liệu thất bại");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox my={3}>
        <Card style={{ height: "100%" }}>
          <Filters
            filters={filters}
            setFilters={setFilters}
            onApply={handleApply}
            onReset={handleReset}
            onSync={handleSync}
            syncing={syncing}
            loading={loading}
          />
          <TableCommon
            loading={loading}
            query={query}
            setQuery={setQuery}
            paginationData={paginationData}
            setPaginationData={setPaginationData}
            table={dataTableState}
            isSorted
            noEndBorder={false}
            pagination={{ variant: "gradient", color: "info" }}
            tableInstance={tableInstance}
          />
          <SoftBox px={3} pb={3} display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
            <SoftTypography variant="caption" color="text">
              Tổng số dòng: {summary.totalRows} - Đã ghi Sổ Cái: {summary.postedToLedgerCount}
            </SoftTypography>
            <SoftTypography variant="caption" color="text" textAlign="right">
              Tổng Nợ: {formatVndIntegerValue(summary.totalDebit || 0)} | Tổng Có: {formatVndIntegerValue(summary.totalCredit || 0)}
            </SoftTypography>
          </SoftBox>
        </Card>
      </SoftBox>
    </DashboardLayout>
  );
}
